import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfigResponse } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createScopeCondition } from '@/components/backupStatus/configurationStatusScope';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { TagsElasticContainer } from '@/components/tags/tagsElasticContainer';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { EntraIdResourceSelectionViewer } from './EntraIdResourceSelectionViewer';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { M365ResourceSelectionViewer } from './M365ResourceSelectionViewer';
import { backupPolicyNativeProvider, isAzureEntraIdPolicyType, isContinuousPolicyType, isGoogleWorkspacePolicyType, isMicrosoft365PolicyType, isNativePolicyType, isPitrPolicyType, isSingleTenantSaasPolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyClassificationSchedule, BackupPolicyContinuousSchedulesTable, BackupPolicyScheduleTimezoneNote, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfigResponse;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfigResponse) => void;
  onDelete?: (policy: BackupPolicyConfigResponse) => void;
  onDuplicate?: (policy: BackupPolicyConfigResponse) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfigResponse) => Promise<boolean>;
}
export function BackupPolicyCard(props) {
  const $ = _c(193);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const {
    InventoryStatusColumns,
    PolicyAndVaultMetadataLabels: t0
  } = useFeatureFlags();
  const PolicyAndVaultMetadataLabels = t0 === undefined ? false : t0;
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  const hasClassificationSchedule = !!props.policy.classificationSchedule;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== InventoryStatusColumns || $[1] !== allBackupPolicyProperties || $[2] !== enabled || $[3] !== isAuthorized || $[4] !== props || $[5] !== snackbar || $[6] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t16;
    if ($[29] !== props.policy.condition) {
      t16 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[29] = props.policy.condition;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    const resourcesScopeType = t16;
    let t17;
    if ($[31] !== props.policy.policyType) {
      t17 = isSingleTenantSaasPolicyType(props.policy.policyType);
      $[31] = props.policy.policyType;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    const isSaasWorkspace = t17;
    let t18;
    if ($[33] !== props || $[34] !== snackbar) {
      t18 = async () => {
        const enabled_0 = props.policy.enabled;
        const updated = await props.onEnabledPolicyUpdate?.(props.policy);
        if (updated === false) {
          return;
        }
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[33] = props;
      $[34] = snackbar;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    togglePolicyEnabledState = t18;
    let t19;
    if ($[36] !== enabled || $[37] !== togglePolicyEnabledState) {
      t19 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[36] = enabled;
      $[37] = togglePolicyEnabledState;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    const onToggleClicked = t19;
    const t20 = props.policy?.id || "";
    let T6;
    let T7;
    let t21;
    let t22;
    let t23;
    let t24;
    let t25;
    let t26;
    if ($[39] !== InventoryStatusColumns || $[40] !== enabled || $[41] !== isAuthorized || $[42] !== isSaasWorkspace || $[43] !== props.onDelete || $[44] !== props.onDuplicate || $[45] !== props.onEdit || $[46] !== props.onEnabledPolicyUpdate || $[47] !== props.policy.attachedResources || $[48] !== props.policy.name || $[49] !== props.policy.policyType || $[50] !== props.processing || $[51] !== t20) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t20],
        property: backupPolicies.name
      };
      T5 = BPCard;
      t14 = !enabled;
      t15 = "backup-policy-card";
      T7 = Stack;
      t24 = "row";
      if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[63] = t25;
      } else {
        t25 = $[63];
      }
      let t27;
      if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[64] = t27;
      } else {
        t27 = $[64];
      }
      let t28;
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[65] = t28;
      } else {
        t28 = $[65];
      }
      let t29;
      if ($[66] !== props.policy.name) {
        t29 = <Stack sx={t28}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[66] = props.policy.name;
        $[67] = t29;
      } else {
        t29 = $[67];
      }
      let t30;
      if ($[68] !== props.policy.policyType) {
        t30 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[68] = props.policy.policyType;
        $[69] = t30;
      } else {
        t30 = $[69];
      }
      if ($[70] !== t29 || $[71] !== t30) {
        t26 = <Stack direction="row" sx={t27}>{t29}{t30}</Stack>;
        $[70] = t29;
        $[71] = t30;
        $[72] = t26;
      } else {
        t26 = $[72];
      }
      T6 = Stack;
      t21 = "row";
      if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = {
          alignItems: "center",
          gap: "16px"
        };
        $[73] = t22;
      } else {
        t22 = $[73];
      }
      t23 = enabled && !isSaasWorkspace && <>{props.processing ? <Loader size="medium" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createScopeCondition("backupPolicyResources", [BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.AccessLost, BackupStatus.Unsupported, BackupStatus.ClassificationFailed, BackupStatus.LimitExceeded], InventoryStatusColumns)])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[39] = InventoryStatusColumns;
      $[40] = enabled;
      $[41] = isAuthorized;
      $[42] = isSaasWorkspace;
      $[43] = props.onDelete;
      $[44] = props.onDuplicate;
      $[45] = props.onEdit;
      $[46] = props.onEnabledPolicyUpdate;
      $[47] = props.policy.attachedResources;
      $[48] = props.policy.name;
      $[49] = props.policy.policyType;
      $[50] = props.processing;
      $[51] = t20;
      $[52] = T5;
      $[53] = T6;
      $[54] = T7;
      $[55] = t14;
      $[56] = t15;
      $[57] = t21;
      $[58] = t22;
      $[59] = t23;
      $[60] = t24;
      $[61] = t25;
      $[62] = t26;
    } else {
      T5 = $[52];
      T6 = $[53];
      T7 = $[54];
      t14 = $[55];
      t15 = $[56];
      t21 = $[57];
      t22 = $[58];
      t23 = $[59];
      t24 = $[60];
      t25 = $[61];
      t26 = $[62];
    }
    let t27;
    if ($[74] !== isAuthorized || $[75] !== isSaasWorkspace || $[76] !== props) {
      t27 = (isSaasWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isSaasWorkspace ? [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[74] = isAuthorized;
      $[75] = isSaasWorkspace;
      $[76] = props;
      $[77] = t27;
    } else {
      t27 = $[77];
    }
    let t28;
    if ($[78] !== enabled || $[79] !== isAuthorized || $[80] !== onToggleClicked || $[81] !== props.onEnabledPolicyUpdate) {
      t28 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[78] = enabled;
      $[79] = isAuthorized;
      $[80] = onToggleClicked;
      $[81] = props.onEnabledPolicyUpdate;
      $[82] = t28;
    } else {
      t28 = $[82];
    }
    let t29;
    if ($[83] !== T6 || $[84] !== t21 || $[85] !== t22 || $[86] !== t23 || $[87] !== t27 || $[88] !== t28) {
      t29 = <T6 direction={t21} sx={t22}>{t23}{t27}{t28}</T6>;
      $[83] = T6;
      $[84] = t21;
      $[85] = t22;
      $[86] = t23;
      $[87] = t27;
      $[88] = t28;
      $[89] = t29;
    } else {
      t29 = $[89];
    }
    if ($[90] !== T7 || $[91] !== t24 || $[92] !== t25 || $[93] !== t26 || $[94] !== t29) {
      t13 = <T7 direction={t24} sx={t25}>{t26}{t29}</T7>;
      $[90] = T7;
      $[91] = t24;
      $[92] = t25;
      $[93] = t26;
      $[94] = t29;
      $[95] = t13;
    } else {
      t13 = $[95];
    }
    T4 = CardContent;
    if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        marginTop: "24px",
        "&&": {
          padding: "0 0 24px"
        }
      };
      $[96] = t12;
    } else {
      t12 = $[96];
    }
    T3 = Stack;
    if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Divider />;
      t10 = {
        gap: "12px"
      };
      $[97] = t10;
      $[98] = t9;
    } else {
      t10 = $[97];
      t9 = $[98];
    }
    let t30;
    if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = {
        px: "24px"
      };
      $[99] = t30;
    } else {
      t30 = $[99];
    }
    let t31;
    if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[100] = t31;
    } else {
      t31 = $[100];
    }
    let t32;
    if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = {
        gap: "24px"
      };
      $[101] = t32;
    } else {
      t32 = $[101];
    }
    let t33;
    if ($[102] !== allBackupPolicyProperties || $[103] !== props.policy.condition || $[104] !== props.policy.exclude || $[105] !== props.policy.include || $[106] !== props.policy.policyType || $[107] !== props.policy.resourceTypes || $[108] !== resourcesScopeType) {
      t33 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isMicrosoft365PolicyType(props.policy.policyType) ? <M365ResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isAzureEntraIdPolicyType(props.policy.policyType) ? <EntraIdResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[102] = allBackupPolicyProperties;
      $[103] = props.policy.condition;
      $[104] = props.policy.exclude;
      $[105] = props.policy.include;
      $[106] = props.policy.policyType;
      $[107] = props.policy.resourceTypes;
      $[108] = resourcesScopeType;
      $[109] = t33;
    } else {
      t33 = $[109];
    }
    let t34;
    if ($[110] !== props.policy.policyType || $[111] !== props.policy.vaultStorageTier) {
      t34 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} vaultStorageTier={props.policy.vaultStorageTier} />;
      $[110] = props.policy.policyType;
      $[111] = props.policy.vaultStorageTier;
      $[112] = t34;
    } else {
      t34 = $[112];
    }
    let t35;
    if ($[113] !== t33 || $[114] !== t34) {
      t35 = <AccordionDetails><Stack sx={t32}>{t33}{t34}</Stack></AccordionDetails>;
      $[113] = t33;
      $[114] = t34;
      $[115] = t35;
    } else {
      t35 = $[115];
    }
    if ($[116] !== props.defaultExpanded || $[117] !== t35) {
      t11 = <Accordion defaultExpanded={props.defaultExpanded} sx={t30}>{t31}{t35}</Accordion>;
      $[116] = props.defaultExpanded;
      $[117] = t35;
      $[118] = t11;
    } else {
      t11 = $[118];
    }
    T2 = Accordion;
    t6 = vaultsSectionExpanded;
    if ($[119] !== vaultsSectionExpanded) {
      t7 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[119] = vaultsSectionExpanded;
      $[120] = t7;
    } else {
      t7 = $[120];
    }
    if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        px: "24px"
      };
      $[121] = t8;
    } else {
      t8 = $[121];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t1 = "row";
    t2 = "w-full";
    if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t4 = <Typography variant="h4">Schedules</Typography>;
      $[122] = t3;
      $[123] = t4;
    } else {
      t3 = $[122];
      t4 = $[123];
    }
    t5 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = InventoryStatusColumns;
    $[1] = allBackupPolicyProperties;
    $[2] = enabled;
    $[3] = isAuthorized;
    $[4] = props;
    $[5] = snackbar;
    $[6] = vaultsSectionExpanded;
    $[7] = T0;
    $[8] = T1;
    $[9] = T2;
    $[10] = T3;
    $[11] = T4;
    $[12] = T5;
    $[13] = t1;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = togglePolicyEnabledState;
  } else {
    T0 = $[7];
    T1 = $[8];
    T2 = $[9];
    T3 = $[10];
    T4 = $[11];
    T5 = $[12];
    t1 = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
    t14 = $[18];
    t15 = $[19];
    t2 = $[20];
    t3 = $[21];
    t4 = $[22];
    t5 = $[23];
    t6 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
    togglePolicyEnabledState = $[28];
  }
  let t16;
  if ($[124] !== T0 || $[125] !== t1 || $[126] !== t2 || $[127] !== t3 || $[128] !== t4 || $[129] !== t5) {
    t16 = <T0 direction={t1} className={t2} sx={t3}>{t4}{t5}</T0>;
    $[124] = T0;
    $[125] = t1;
    $[126] = t2;
    $[127] = t3;
    $[128] = t4;
    $[129] = t5;
    $[130] = t16;
  } else {
    t16 = $[130];
  }
  let t17;
  if ($[131] !== T1 || $[132] !== t16) {
    t17 = <T1>{t16}</T1>;
    $[131] = T1;
    $[132] = t16;
    $[133] = t17;
  } else {
    t17 = $[133];
  }
  let t18;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[134] = t18;
  } else {
    t18 = $[134];
  }
  let t19;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      gap: "24px"
    };
    $[135] = t19;
  } else {
    t19 = $[135];
  }
  let t20;
  if ($[136] !== props.policy) {
    t20 = <BackupPolicyScheduleTimezoneNote policy={props.policy} />;
    $[136] = props.policy;
    $[137] = t20;
  } else {
    t20 = $[137];
  }
  let t21;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      gap: "24px"
    };
    $[138] = t21;
  } else {
    t21 = $[138];
  }
  let t22;
  if ($[139] !== hasClassificationSchedule) {
    t22 = hasClassificationSchedule && <Typography variant="body1">Backup schedules</Typography>;
    $[139] = hasClassificationSchedule;
    $[140] = t22;
  } else {
    t22 = $[140];
  }
  let t23;
  if ($[141] !== props.policy || $[142] !== props.vaults) {
    t23 = isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />;
    $[141] = props.policy;
    $[142] = props.vaults;
    $[143] = t23;
  } else {
    t23 = $[143];
  }
  let t24;
  if ($[144] !== t22 || $[145] !== t23) {
    t24 = <Stack sx={t21}>{t22}{t23}</Stack>;
    $[144] = t22;
    $[145] = t23;
    $[146] = t24;
  } else {
    t24 = $[146];
  }
  let t25;
  if ($[147] !== hasClassificationSchedule || $[148] !== props.policy) {
    t25 = hasClassificationSchedule && <BackupPolicyClassificationSchedule policy={props.policy} />;
    $[147] = hasClassificationSchedule;
    $[148] = props.policy;
    $[149] = t25;
  } else {
    t25 = $[149];
  }
  let t26;
  if ($[150] !== t20 || $[151] !== t24 || $[152] !== t25) {
    t26 = <AccordionDetails sx={t18}><Stack sx={t19}>{t20}{t24}{t25}</Stack></AccordionDetails>;
    $[150] = t20;
    $[151] = t24;
    $[152] = t25;
    $[153] = t26;
  } else {
    t26 = $[153];
  }
  let t27;
  if ($[154] !== T2 || $[155] !== t17 || $[156] !== t26 || $[157] !== t6 || $[158] !== t7 || $[159] !== t8) {
    t27 = <T2 expanded={t6} onChange={t7} sx={t8}>{t17}{t26}</T2>;
    $[154] = T2;
    $[155] = t17;
    $[156] = t26;
    $[157] = t6;
    $[158] = t7;
    $[159] = t8;
    $[160] = t27;
  } else {
    t27 = $[160];
  }
  let t28;
  if ($[161] !== PolicyAndVaultMetadataLabels || $[162] !== props.policy.labels) {
    t28 = PolicyAndVaultMetadataLabels && <Accordion sx={{
      px: "24px"
    }}><AccordionSummary><Typography variant="h4">{"Labels"}</Typography></AccordionSummary><AccordionDetails>{props.policy.labels?.length ? <TagsElasticContainer tags={props.policy.labels} expandable={true} /> : <Typography variant="body1">{"No labels"}</Typography>}</AccordionDetails></Accordion>;
    $[161] = PolicyAndVaultMetadataLabels;
    $[162] = props.policy.labels;
    $[163] = t28;
  } else {
    t28 = $[163];
  }
  let t29;
  if ($[164] !== T3 || $[165] !== t10 || $[166] !== t11 || $[167] !== t27 || $[168] !== t28 || $[169] !== t9) {
    t29 = <T3 divider={t9} sx={t10}>{t11}{t27}{t28}</T3>;
    $[164] = T3;
    $[165] = t10;
    $[166] = t11;
    $[167] = t27;
    $[168] = t28;
    $[169] = t9;
    $[170] = t29;
  } else {
    t29 = $[170];
  }
  let t30;
  if ($[171] !== T4 || $[172] !== t12 || $[173] !== t29) {
    t30 = <T4 sx={t12}>{t29}</T4>;
    $[171] = T4;
    $[172] = t12;
    $[173] = t29;
    $[174] = t30;
  } else {
    t30 = $[174];
  }
  let t31;
  if ($[175] !== props.policy.name) {
    t31 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[175] = props.policy.name;
    $[176] = t31;
  } else {
    t31 = $[176];
  }
  let t32;
  if ($[177] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setDisablePolicyDialogOpen(false);
    $[177] = t32;
  } else {
    t32 = $[177];
  }
  let t33;
  if ($[178] !== togglePolicyEnabledState) {
    t33 = async () => {
      await togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[178] = togglePolicyEnabledState;
    $[179] = t33;
  } else {
    t33 = $[179];
  }
  let t34;
  if ($[180] !== disablePolicyDialogOpen || $[181] !== t31 || $[182] !== t33) {
    t34 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t31} onCancel={t32} onConfirm={t33} buttonTitle="Disable Policy" />;
    $[180] = disablePolicyDialogOpen;
    $[181] = t31;
    $[182] = t33;
    $[183] = t34;
  } else {
    t34 = $[183];
  }
  let t35;
  if ($[184] !== t13 || $[185] !== t30 || $[186] !== t34) {
    t35 = <>{t13}{t30}{t34}</>;
    $[184] = t13;
    $[185] = t30;
    $[186] = t34;
    $[187] = t35;
  } else {
    t35 = $[187];
  }
  let t36;
  if ($[188] !== T5 || $[189] !== t14 || $[190] !== t15 || $[191] !== t35) {
    t36 = <T5 disabled={t14} data-testid={t15}>{t35}</T5>;
    $[188] = T5;
    $[189] = t14;
    $[190] = t15;
    $[191] = t35;
    $[192] = t36;
  } else {
    t36 = $[192];
  }
  return t36;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}